// src/pages/auth/CollaboratorVerification.tsx

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { z } from "zod";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";

import { verifyEmailOtpSchema } from "@backend/src/schemas/authSchema";
import { useUser } from "@/hooks/useUser";
import useVerifyEmailMutation from "@/api/mutation/useVerifyEmailMutation";
import useResendVerifyEmailOtpMutation from "@/api/mutation/useResendVerifyEmailOtpMutation";
import useCurrentUserQuery from "@/api/query/useCurrentUserQuery";

import InputText from "@/components/common/InputText";
import ErrorMessage from "@/components/common/ErrorMessage";
import FormLabel from "@/components/common/FormLabel";
import { Button } from "@/components/ui/button";
import BackgroundImage from "@/components/layout/BackgroundImage";

import NewLogoTitle from "@/assets/svg/NewLogoTitle.svg";
import UserVerifiedIcon from "@/assets/svg/UserVerified.svg";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import { errorConfig } from "@/lib/toastError";
import useTimer from "@/hooks/useTimer";
import useTempCollaboratorLogin from "@/api/query/useCollaboratorVerification";
import Loader from "@/components/common/Loader";

function CollaboratorVerification() {
  const { userId } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const tempCollaboratorLogin = useTempCollaboratorLogin(userId ?? "");
  const verifyEmailMutation = useVerifyEmailMutation();
  const resendOtpMutation = useResendVerifyEmailOtpMutation();
  const { refetch, status,isFetching ,  isRefetching } = useCurrentUserQuery();

  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [count, setCount] = useState(0);

  const localLastSent = localStorage.getItem("lastSent");
  const [lastSentTime, setLastSentTime] = useState<number | null>(
    localLastSent ? parseInt(localLastSent) : null
  );

  const { time, resetTimer } = useTimer(
    lastSentTime
      ? Math.max(60 - Math.floor((Date.now() - lastSentTime) / 1000), 0)
      : 0
  );
  const sendOtp = async () => {
    setIsResending(true);

    await tempCollaboratorLogin.refetch();
    await refetch();

    resendOtpMutation.mutate(null, {
      onSuccess: (data) => {
        const message = data?.data.message;

        if (!message) {
          toast.error("Failed to send OTP, Please try again after 1 minute");
          setIsResending(false);
          return;
        }

        const now = Date.now();
        localStorage.setItem("lastSent", `${now}`);
        setLastSentTime(now);
        resetTimer(60);

        toast.success(message);
        setResendError("");
        setIsResending(false);

        // ✅ Save last login on successful OTP send
        if (user?.userId) {
          localStorage.setItem(
            "lastLogin",
            JSON.stringify({ userId: user.userId, time: now })
          );
        }
      },
      onError: (err: any) => {
        setIsResending(false);
        setResendError("Failed to resend OTP");
        toast.error(errorKeyFormate(err?.response?.data?.message), errorConfig);
      },
    });
  };
  const hasSentOtp = useRef(false);
  useEffect(() => {
       const lastLoginData = JSON.parse(
        localStorage.getItem("lastLogin") || "{}"
      );
      const lastLoginTime = lastLoginData?.time || 0;
      const lastLoginUserId = lastLoginData?.userId;

      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
      const loggedInRecently =
        user?.userId === lastLoginUserId &&
        Date.now() - lastLoginTime <= sevenDaysInMs;
    if (status === "success") {
      if (user?.isVerified) {
        setOtpVerified(true);
        navigate("/organisation");
        return;
      }
      if (!user && !isResending && !hasSentOtp.current && !loggedInRecently) {
        const now = Date.now();
        const timeDiffInSeconds = lastSentTime
          ? (now - lastSentTime) / 1000
          : Infinity;

        if (timeDiffInSeconds >= 60) {
          hasSentOtp.current = true;
          setIsResending(true);
          sendOtp();
        } else {
          console.log("OTP was sent less than a minute ago. Skipping resend.");
        }
      }
    }else{
      if (!user && !isResending && !hasSentOtp.current && !loggedInRecently) {
        const now = Date.now();
        const timeDiffInSeconds = lastSentTime
          ? (now - lastSentTime) / 1000
          : Infinity;

        if (timeDiffInSeconds >= 60) {
          hasSentOtp.current = true;
          setIsResending(true);
          sendOtp();
        } else {
          console.log("OTP was sent less than a minute ago. Skipping resend.");
        }
      }
    }
  }, [user, status, lastSentTime, navigate]);

  useEffect(() => {
    if (isVerifying) {
      const timer = setInterval(() => {
        setCount((prev) => prev + 1);
      }, 1000);

      if (count >= 30) {
        clearInterval(timer);
        navigate("/organisation", { state: { showVideo: true } });
      }

      return () => clearInterval(timer);
    }
  }, [count, isVerifying]);

  const formik = useFormik<z.infer<typeof verifyEmailOtpSchema>>({
    initialValues: { otp: "" },
    validationSchema: toFormikValidationSchema(verifyEmailOtpSchema),
    onSubmit: (values, helpers) => {
      setIsVerifying(true);
      verifyEmailMutation.mutate(values, {
        onSuccess(data) {
          toast.success(data.data.message);
          refetch();
          setOtpVerified(true);
        },
        onError(error) {
          setIsVerifying(false);
          if (isAxiosError(error)) {
            if (
              error.response?.status === 400 &&
              Array.isArray(error.response?.data?.errors)
            ) {
              error.response.data.errors.forEach((item) => {
                helpers.setFieldError(item.path[0], item.message);
              });
            } else {
              toast.error(
                errorKeyFormate(error.response?.data.message + ""),
                errorConfig
              );
            }
          }
        },
      });
    },
  });

  return (
    <div className="flex flex-col px-2 items-center min-h-screen py-6 justify-center bg-secondary-950">
     {(isFetching ||  isRefetching) &&  <Loader className=""/>}
      <BackgroundImage />
      <div className="absolute bg-primary-500/20 rounded-br-3xl px-40 rounded-bl-3xl mb-5 h-24 w-auto top-0 left-1/2 -translate-x-1/2 flex justify-center items-center">
        <img src={NewLogoTitle} className="h-10" />
      </div>

      <div className="w-[min(450px,100%)] space-y-4 py-4 bg-white shadow-md sm:max-w-lg sm:rounded-lg z-10">
        <div className="px-4">
          <h3 className="text-2xl text-center font-bold text-primary-900">
            {!otpVerified ? t("Verify Email") : t("Email Verified")}
          </h3>
        </div>
        <hr />

        {!otpVerified ? (
          <form onSubmit={formik.handleSubmit} className="px-4">
            <p className="mt-4 text-gray-600">
              {t("We have sent an OTP (One time password) to your email")}{" "}
              <span className="font-bold">{user?.email}</span>
            </p>

            <div className="relative w-full mt-4">
              <FormLabel htmlFor="otp">{t("One time password")}</FormLabel>
              <InputText
                name="otp"
                placeholder="Please enter OTP"
                value={formik.values.otp}
                onChange={formik.handleChange}
              />
              <ErrorMessage>
                {formik.touched.otp && formik.errors.otp}
              </ErrorMessage>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-danger text-sm">{resendError}</span>
              <Button
                variant="primary_outline"
                className="h-auto py-1 px-2"
                onClick={() => {
                  setIsResending(true);
                  resendOtpMutation.mutate(null, {
                    onSuccess(data) {
                      localStorage.setItem(
                        "lastLogin",
                        JSON.stringify({
                          userId: user?.userId,
                          time: Date.now(),
                        })
                      );
                      const now = Date.now();
                      localStorage.setItem("lastSent", `${now}`);
                      setLastSentTime(now);
                      resetTimer(60);
                      toast.success(data.data.message);
                      setResendError("");
                      setIsResending(false);
                    },
                    onError(error) {
                      setIsResending(false);
                      setResendError("Failed to resend OTP");
                      toast.error(
                        errorKeyFormate(error.response?.data.message + ""),
                        errorConfig
                      );
                    },
                  });
                }}
                isLoading={isResending}
                disabled={time !== 0 || isResending}
              >
                {time === 0 ? t("Resend OTP") : `${t("Resend OTP")} in ${time}`}
              </Button>
            </div>

            <div className="flex items-center mt-1">
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                isLoading={isVerifying}
                disabled={isVerifying}
              >
                {t("Verify")}
              </Button>
            </div>

            <p className="text-[0.7rem] mt-5">
              {t(
                "Check your inbox and spam folder. If you don’t receive the OTP within 15 min, your email provider might have blocked it. Try using a different email."
              )}
            </p>
          </form>
        ) : (
          <div className="rounded-lg p-3 bg-white flex flex-col items-center justify-around">
            <img src={UserVerifiedIcon} alt="Verified" width={50} />
            <p className="mt-4 text-lg text-gray-600 text-center">
              {t("Congratulations!")}
              <br />
              {t("Your email has been successfully verified.")}
            </p>
            <div className="mt-3 w-full">
              <Button
                variant="primary"
                onClick={() =>
                  navigate("/organisation", { state: { showVideo: true } })
                }
                className="w-full"
              >
                {t("Access my space")}
              </Button>
              <p className="text-center text-xs mt-5">
                {t("Automatically redirect in")}{" "}
                {(count - 30).toString().substring(1)} {t("seconds")}.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CollaboratorVerification;
